import { useState, useCallback, useEffect } from "react";
import { authFetch } from "../../App";
import { toast } from "sonner";
import {
  ChevronRight, ChevronDown, ArrowLeft, Loader2, Database, Layers, Trash2,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { BlockCalculation } from "./GAEBBlockCalc";
import { PriceDatabase } from "./GAEBPriceDB";

const API = "/api";
const fmt = (n) => n != null ? n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "\u2014";

export function ProjectCalculation({ projectId, onBack }) {
  const [blocks, setBlocks] = useState([]);
  const [projectSummary, setProjectSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedBlocks, setExpandedBlocks] = useState(new Set());
  const [showPriceDB, setShowPriceDB] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([
        authFetch(`${API}/gaeb/projects/${projectId}`),
        authFetch(`${API}/gaeb/projects/${projectId}/summary`),
      ]);
      if (r1.ok) {
        const data = await r1.json();
        setBlocks(data.blocks || []);
        setExpandedBlocks(new Set((data.blocks || []).map(b => b.id)));
      }
      if (r2.ok) setProjectSummary(await r2.json());
    } catch {}
    setLoading(false);
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const toggleBlock = (blockId) => {
    setExpandedBlocks((prev) => {
      const next = new Set(prev);
      if (next.has(blockId)) next.delete(blockId); else next.add(blockId);
      return next;
    });
  };

  const handleRemoveBlock = async (block) => {
    if (!window.confirm(`Block "${block.block_name}" vom Projekt entfernen?\n\nDie Original-GAEB-Datei bleibt erhalten und kann erneut zugewiesen werden.`)) return;
    try {
      const res = await authFetch(`${API}/gaeb/blocks/${block.id}`, { method: "DELETE" });
      if (res.ok) {
        toast.success(`Block "${block.block_name}" entfernt`);
        load();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Fehler");
      }
    } catch { toast.error("Fehler"); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  if (!blocks.length) return <div className="text-center py-20 text-gray-500">Keine GAEB-Daten gefunden</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-1.5 hover:bg-gray-100 rounded-lg" data-testid="gaeb-calc-back"><ArrowLeft className="w-4 h-4" /></button>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold text-gray-900 truncate">Projekt-Kalkulation</h2>
          <p className="text-xs text-gray-500">{blocks.length} GAEB-Block{blocks.length !== 1 ? "e" : ""}</p>
        </div>
        <Button size="sm" onClick={() => setShowPriceDB(true)} variant="outline" className="gap-1.5 text-[11px] h-8" data-testid="gaeb-price-db-btn">
          <Database className="w-3.5 h-3.5" /> Preisdatenbank
        </Button>
      </div>

      {projectSummary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: "Gesamt", value: projectSummary.total_positions, sub: `${projectSummary.block_count} Block${projectSummary.block_count !== 1 ? "e" : ""}` },
            { label: "Sichtbar", value: projectSummary.visible_positions, sub: "Positionen" },
            { label: "Kalkuliert", value: projectSummary.calculated_positions, sub: "mit EP" },
            { label: "Netto", value: `${fmt(projectSummary.total_netto)} EUR`, sub: `zzgl. ${projectSummary.ust_pct}% USt.` },
            { label: "Brutto", value: `${fmt(projectSummary.total_brutto)} EUR`, sub: "Gesamtsumme", highlight: true },
          ].map(({ label, value, sub, highlight }) => (
            <div key={label} className={`rounded-lg border p-3 ${highlight ? "bg-slate-800 border-slate-700" : "bg-white border-gray-200"}`} data-testid={`project-summary-${label.toLowerCase()}`}>
              <p className={`text-[10px] uppercase tracking-wide mb-1 ${highlight ? "text-slate-400" : "text-gray-400"}`}>{label}</p>
              <p className={`text-sm font-bold ${highlight ? "text-white" : "text-gray-900"}`}>{value}</p>
              <p className={`text-[10px] ${highlight ? "text-slate-400" : "text-gray-400"}`}>{sub}</p>
            </div>
          ))}
        </div>
      )}

      {projectSummary?.block_summaries?.length > 1 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {projectSummary.block_summaries.map((bs) => (
            <div key={bs.block_id} className="bg-white rounded-lg border border-gray-200 p-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                <Layers className="w-4 h-4 text-slate-500" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-800 truncate">{bs.block_name}</p>
                <p className="text-[11px] text-gray-400">{bs.total_positions} Pos. | Netto: {fmt(bs.total_netto)} EUR</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {blocks.map((block) => (
        <div key={block.id} className="border border-gray-200 rounded-xl overflow-hidden" data-testid={`gaeb-block-${block.id}`}>
          <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-slate-50 to-gray-50 cursor-pointer hover:from-slate-100 hover:to-gray-100 transition-colors" onClick={() => toggleBlock(block.id)}>
            {expandedBlocks.has(block.id) ? <ChevronDown className="w-5 h-5 text-slate-500" /> : <ChevronRight className="w-5 h-5 text-slate-500" />}
            <div className="w-7 h-7 rounded-lg bg-slate-800 flex items-center justify-center shrink-0">
              <span className="text-white text-xs font-bold">{block.block_number || "?"}</span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900 truncate">{block.block_name}</p>
              <p className="text-[11px] text-gray-400">{block.filename || block.lv_name} | {(block.positions || []).length} Positionen</p>
            </div>
            <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-1 rounded-md">Block {block.block_number}</span>
            <button onClick={(e) => { e.stopPropagation(); handleRemoveBlock(block); }} className="p-1.5 hover:bg-red-50 rounded-lg transition-colors ml-1" title="Block vom Projekt entfernen" data-testid={`block-remove-${block.id}`}>
              <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
            </button>
          </div>
          {expandedBlocks.has(block.id) && (
            <div className="p-4 border-t border-gray-100">
              <BlockCalculation block={block} onReload={load} />
            </div>
          )}
        </div>
      ))}

      {showPriceDB && <PriceDatabase onClose={() => setShowPriceDB(false)} />}
    </div>
  );
}
